import { useEffect, useRef, useState } from "react";
import { expeditionStageById, unitById, zombieById } from "@zend/content";
import { AssetIcon } from "../assets/Icon.js";
import { StatIcon } from "../assets/gameIcons.js";
import { pick } from "../i18n/index.js";
import { navigate } from "../router.js";
import { Button } from "../ui/Button.js";
import { useUnits } from "../units/state.js";
import { unitsApi } from "../units/api.js";
import { SQUAD_SLOT_KEYS, type ExpeditionStageView } from "../units/types.js";
import { worldApi } from "../world/api.js";
import { FuelSheet } from "../world/FuelSheet.js";
import { Icon } from "../ui/Icon.js";
import "./briefing.css";

const words = (de: string, en: string) => pick({ de, en });

/** Inspection never mutates the formation or spends fuel. The selected real squad is committed explicitly. */
export function ExpeditionBriefing({ stage, stamina, attacking, onAttack }: {
  stage: ExpeditionStageView; stamina: number; attacking: boolean; onAttack: (slot: number) => void;
}) {
  const roster = useUnits((s) => s.roster);
  const selected = useUnits((s) => s.squadSlot);
  const refresh = useUnits((s) => s.refreshRoster);
  const run = useUnits((s) => s.run);
  const [slot, setSlot] = useState(selected);
  const [deployed, setDeployed] = useState<number[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  const [filling, setFilling] = useState(false);
  const [fuelOpen, setFuelOpen] = useState(false);
  const fillLock = useRef(false);
  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout>;
    const check = async () => {
      try {
        const [, marches] = await Promise.all([refresh(), worldApi.marches()]);
        if (alive) { setDeployed(marches.filter((m) => m.state !== "done").map((m) => m.squadSlot)); setFailed(false); }
      } catch { if (alive) { setDeployed(null); setFailed(true); } }
      if (alive) timer = setTimeout(() => void check(), 5000);
    };
    void check();
    return () => { alive = false; clearTimeout(timer); };
  }, [refresh, retry]);
  const squad = roster?.squads.find((s) => s.slot === slot);
  const members = SQUAD_SLOT_KEYS.flatMap((key) => {
    const unit = roster?.units.find((u) => u.id === squad?.members[key]);
    return unit ? [{ key, unit }] : [];
  });
  const away = !!squad?.siegeRunId || deployed?.includes(slot);
  const repairing = members.some(({ unit }) => unit.repair);
  const ready = !!squad?.ready && !away && !repairing && deployed !== null;
  const enemies = expeditionStageById(stage.id).enemy.members;
  const locked = attacking || filling;
  const fill = async () => {
    if (fillLock.current || locked || away || deployed === null) return;
    fillLock.current = true; setFilling(true);
    try { await run(() => unitsApi.autofill(slot)); }
    finally { fillLock.current = false; setFilling(false); }
  };
  return <div className="expedition-briefing" data-testid="expedition-briefing">
    <section className="briefing-enemy">
      <div className="briefing-heading"><h3>{words("Dein Gegner", "Your enemy")}</h3><span><StatIcon stat="power" /> {stage.power}</span></div>
      <div className="briefing-units">{enemies.map((enemy, index) => <div className="briefing-unit" key={index}>
        <AssetIcon id={enemy.zombieId} size={92} />
        <strong>{pick(zombieById(enemy.zombieId).name)}</strong><small>Lv {enemy.level}</small>
        <small>{pick(zombieById(enemy.zombieId).abilities.at(-1)!.name)}</small>
      </div>)}</div>
    </section>
    <section>
      <div className="briefing-heading"><h3>{words("Dein Trupp", "Your squad")}</h3><span><StatIcon stat="power" /> {squad?.power ?? 0}</span></div>
      <div className="briefing-squads" aria-label={words("Trupp auswählen", "Select squad")}>
        {roster?.squads.map((s) => <Button key={s.slot} variant={s.slot === slot ? "gold" : "ghost"} aria-pressed={s.slot === slot} disabled={locked} onClick={() => { setSlot(s.slot); useUnits.getState().setSquadSlot(s.slot); }} data-testid={`briefing-squad-${s.slot}`}>{s.name}</Button>)}
      </div>
      <div className="briefing-units" data-testid="briefing-members">{members.map(({ key, unit }) => <div key={key} className="briefing-unit" data-unavailable={unit.wrecked || !!unit.repair}>
        <AssetIcon id={unit.unitId} size={92} portrait />
        <strong>{pick(unitById(unit.unitId).name)}</strong><small>Lv {unit.level} · {Math.round(unit.hpPct)}% HP</small>
        {unit.repair ? <small>{words("In Reparatur", "Repairing")}</small> : unit.wrecked ? <small>{words("Ausgefallen", "Wrecked")}</small> : null}
      </div>)}</div>
      {!members.length ? <p>{words("Noch keine Einheiten zugeteilt.", "No units assigned yet.")}</p> : null}
      <div className="briefing-squads">
        <Button disabled={locked || away || deployed === null} loading={filling} onClick={() => void fill()} data-testid="briefing-autofill">{words("Automatisch besetzen", "Auto-fill squad")}</Button>
        <Button disabled={locked} onClick={() => { useUnits.getState().setSquadSlot(slot); navigate("/app/units/squads"); }}>{words("Trupp bearbeiten", "Edit squad")}</Button>
      </div>
      {away ? <p role="status">{words("Dieser Trupp ist bereits im Einsatz.", "This squad is already deployed.")}</p> : repairing ? <p role="status">{words("Eine Einheit wird repariert. Wähle einen anderen Trupp.", "A unit is repairing. Choose another squad.")}</p> : !squad?.ready && members.length ? <p role="status">{words("Du brauchst einen kampffähigen Kämpfer oder Kommandanten.", "Assign a battle-ready fighter or commander.")}</p> : null}
    </section>
    {deployed === null ? <p role="status">{failed ? <Button onClick={() => setRetry((n) => n + 1)}>{words("Verfügbarkeit erneut prüfen", "Check availability again")}</Button> : words("Truppverfügbarkeit wird geprüft …", "Checking squad availability…")}</p> : null}
    <div className="briefing-commit">
      <span><StatIcon stat="stamina" /> {stage.stamina} · {words("Vorrat", "Available")}: {stamina} <Button className="fuel-open" onClick={() => setFuelOpen(true)} aria-label={words("Treibstoff nachfüllen", "Refill fuel")} data-testid="fuel-open"><Icon name="plus" size={14} /></Button></span>
      {fuelOpen ? <FuelSheet onClose={() => setFuelOpen(false)} /> : null}
      <Button variant="gold" loading={attacking} disabled={locked || !ready || stamina < stage.stamina} onClick={() => onAttack(slot)} data-testid="story-attack">{words("Angriff starten", "Launch attack")}</Button>
    </div>
  </div>;
}
